import dayjs from 'dayjs';

// Minimal task interface for sorting purposes
export interface ISortableTask {
    dueDate?: string;
    displayOrder?: number;
    createdAt: string;
}

/**
 * Check if a due date is date-only format (YYYY-MM-DD) vs datetime
 */
const isDateOnly = (dueDate?: string): boolean => {
    return dueDate?.length === 10 && /^\d{4}-[01]\d-[0-3]\d$/.test(dueDate);
};

/**
 * TaskSorter handles sorting of tasks within date groups.
 */
export class TaskSorter {
    /**
     * Sort tasks according to the complex sorting rules:
     * - Time-based tasks first (sorted by time, then displayOrder)
     * - Date-only tasks second (sorted by displayOrder or createdAt)
     */
    sortTasks<T extends ISortableTask>(tasks: T[]): T[] {
        return [...tasks].sort((a, b) => this.compare(a, b));
    }

    /**
     * Compare two tasks using the sorting rules.
     * This can be used as a comparator function directly.
     * Returns negative if a < b, positive if a > b, 0 if equal.
     */
    compare(a: ISortableTask, b: ISortableTask): number {
        // if tasks have no due date, fall back to createdAt
        const aDateTime = a.dueDate ?? a.createdAt;
        const bDateTime = b.dueDate ?? b.createdAt;

        // use end of day (local time) for tasks without time
        const aSort = isDateOnly(aDateTime) ? dayjs(aDateTime).endOf('d').toISOString() : aDateTime;
        const bSort = isDateOnly(bDateTime) ? dayjs(bDateTime).endOf('d').toISOString() : bDateTime;

        // if different date/time, sort reverse chronologically
        const result = aSort.localeCompare(bSort);
        if (result !== 0) {
            return result;
        }

        // if same date/time, use displayOrder, falling back to createdAt
        const aOrder = a.displayOrder ?? dayjs(a.createdAt).valueOf();
        const bOrder = b.displayOrder ?? dayjs(b.createdAt).valueOf();
        return Math.sign(aOrder - bOrder);
    }
}
